import { formatDate } from 'utils/func';

const COLORS = {
  planned: '#03A9F4',
  tlb: '#016593',
  contracted: '#4ec6fd',
};

const niceCeil = value => {
  if (!value || value <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / pow;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return nice * pow;
};

const formatHours = value => Math.round(value * 10) / 10;

/**
 * Graphique « Cumulative flight time per stages » :
 * - barres groupées par stage : heures planned et TLB cumulées
 * - ligne en escalier : heures contractuelles cumulées (palier par stage effectué)
 */
const CumulativeStageChart = ({ stages, points }) => {
  const width = 960;
  const height = 420;
  const margin = { top: 30, right: 30, bottom: 90, left: 70 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  if (!stages || stages.length === 0) {
    return <p className="empty">Aucun stage disponible</p>;
  }

  const maxValue = points.reduce(
    (max, point) => Math.max(max, point.plannedHours, point.tlbHours, point.contractedHours),
    0,
  );
  const yMax = niceCeil(maxValue * 1.15);
  const tickCount = 5;

  const groupWidth = innerWidth / stages.length;
  const xCenter = index => margin.left + groupWidth * index + groupWidth / 2;
  const y = value => margin.top + innerHeight - (value / yMax) * innerHeight;

  const barWidth = Math.min(30, groupWidth * 0.32);
  const labelEvery = Math.max(1, Math.ceil(stages.length / 14));

  // Ligne en escalier : on monte d'un palier à chaque stage effectué
  let linePath = '';
  points.forEach((point, index) => {
    if (index === 0) {
      linePath += `M ${xCenter(0)} ${y(point.contractedHours)}`;
    } else {
      linePath += ` L ${xCenter(index)} ${y(points[index - 1].contractedHours)} L ${xCenter(index)} ${y(
        point.contractedHours,
      )}`;
    }
  });

  return (
    <svg className="cumulativeChart" viewBox={`0 0 ${width} ${height}`} role="img">
      {/* grille horizontale + axe Y (heures) */}
      {Array.from({ length: tickCount + 1 }).map((_, index) => {
        const value = (yMax / tickCount) * index;
        return (
          <g key={`grid-${index}`}>
            <line x1={margin.left} x2={margin.left + innerWidth} y1={y(value)} y2={y(value)} className="gridLine" />
            <text x={margin.left - 10} y={y(value) + 4} textAnchor="end" className="axisLabel">
              {formatHours(value)}
            </text>
          </g>
        );
      })}
      <text x={margin.left - 55} y={margin.top - 12} className="axisTitle">
        Heures
      </text>

      {/* barres cumulées planned / TLB */}
      {points.map((point, index) => {
        const center = xCenter(index);
        const plannedHeight = innerHeight * (point.plannedHours / yMax);
        const tlbHeight = innerHeight * (point.tlbHours / yMax);
        return (
          <g key={`bars-${point.stageId}`}>
            <rect
              x={center - barWidth - 2}
              y={margin.top + innerHeight - plannedHeight}
              width={barWidth}
              height={plannedHeight}
              fill={COLORS.planned}
            >
              <title>{`${stages[index].name} - Planned: ${point.plannedHours} h`}</title>
            </rect>
            <rect
              x={center + 2}
              y={margin.top + innerHeight - tlbHeight}
              width={barWidth}
              height={tlbHeight}
              fill={COLORS.tlb}
            >
              <title>{`${stages[index].name} - TLB: ${point.tlbHours} h`}</title>
            </rect>
          </g>
        );
      })}

      {/* ligne des heures contractuelles */}
      {linePath && <path d={linePath} className="contractLine" />}
      {points.map((point, index) => {
        const previous = index > 0 ? points[index - 1].contractedHours : null;
        const isStep = previous === null || point.contractedHours !== previous;
        const isLast = index === points.length - 1;
        return (
          <g key={`contract-${point.stageId}`}>
            <circle cx={xCenter(index)} cy={y(point.contractedHours)} r={3.5} className="contractDot" />
            {(isStep || isLast) && (
              <text x={xCenter(index)} y={y(point.contractedHours) - 10} textAnchor="middle" className="contractLabel">
                {formatHours(point.contractedHours)}
              </text>
            )}
          </g>
        );
      })}

      {/* axe X (stages) */}
      <line
        x1={margin.left}
        x2={margin.left + innerWidth}
        y1={margin.top + innerHeight}
        y2={margin.top + innerHeight}
        className="axisLine"
      />
      {stages.map((stage, index) =>
        index % labelEvery === 0 ? (
          <g key={`label-${stage.id}`}>
            <line
              x1={xCenter(index)}
              x2={xCenter(index)}
              y1={margin.top + innerHeight}
              y2={margin.top + innerHeight + 5}
              className="tick"
            />
            <text
              x={xCenter(index)}
              y={margin.top + innerHeight + 18}
              textAnchor="end"
              transform={`rotate(-35 ${xCenter(index)} ${margin.top + innerHeight + 18})`}
              className="axisLabel"
            >
              {/* Date formatée en heure locale : la partie UTC de l'ISO
                  correspond à la veille pour les dates à minuit (TZ Paris). */}
              <title>{stage.date ? formatDate(new Date(stage.date)) : ''}</title>
              {stage.name}
            </text>
          </g>
        ) : null,
      )}
    </svg>
  );
};

export default CumulativeStageChart;
